import { SelectedFilters } from '@lidea/shared/data-access/core';

export const SYNC_JOB_STORAGE_KEY = 'lidea_sync_job_v1';
export const CHUNK_SIZE = 25;
export const SYNC_CONCURRENCY = 4;
export const HEATMAP_PLOT_THRESHOLD = 200;
export const MAX_SYNC_ERRORS = 50;
export const SYNC_JOB_VERSION = 1;

export type SyncPhase =
  | 'idle'
  | 'listing'
  | 'plots'
  | 'analyses'
  | 'heatmaps'
  | 'completed'
  | 'completed_with_errors'
  | 'failed'
  | 'paused';

export type SyncErrorEntry = {
  at: number;
  phase: SyncPhase;
  entityType: 'plot' | 'analysis' | 'heatmap' | 'listing';
  entityId: string;
  message: string;
};

export type SyncScope = {
  campaignId: number;
  managerId?: number;
  managerLabel?: string;
  filters: SelectedFilters;
  userRole: string;
};

export type SyncJob = {
  id: string;
  version: typeof SYNC_JOB_VERSION;
  status: SyncPhase;
  createdAt: number;
  updatedAt: number;
  scope: SyncScope;
  plotIds: number[];
  pendingPlotIds: number[];
  pendingAnalysisIds: number[];
  imageryIndexes: string[];
  pendingHeatmapTasks: string[];
  totals: {
    plots: number;
    analyses: number;
    heatmapTasks: number;
  };
  completedPlots: number;
  completedAnalyses: number;
  completedHeatmapTasks: number;
  options: {
    includeHeatmaps: boolean;
    chunkSize: number;
    concurrency: number;
  };
  errors: SyncErrorEntry[];
};

export type SyncJobProgress = {
  phase: SyncPhase;
  plotsPercent: number;
  analysesPercent: number;
  heatmapsPercent: number;
  remainingPlots: number;
  remainingAnalyses: number;
  remainingHeatmaps: number;
  completedPlots: number;
  totalPlots: number;
  completedAnalyses: number;
  totalAnalyses: number;
  completedHeatmaps: number;
  totalHeatmaps: number;
};

export const isActiveSyncPhase = (status: SyncPhase): boolean =>
  status === 'listing' ||
  status === 'plots' ||
  status === 'analyses' ||
  status === 'heatmaps';

export const isResumableSyncPhase = (status: SyncPhase): boolean =>
  isActiveSyncPhase(status) || status === 'paused';

export const isTerminalSyncPhase = (status: SyncPhase): boolean =>
  status === 'completed' ||
  status === 'completed_with_errors' ||
  status === 'failed';

export const isSuccessfulSyncPhase = (status: SyncPhase): boolean =>
  status === 'completed' || status === 'completed_with_errors';

export const computeSyncProgress = (job: SyncJob): SyncJobProgress => {
  const plotsPercent =
    job.totals.plots > 0
      ? Math.round((job.completedPlots / job.totals.plots) * 100)
      : 0;
  const analysesPercent =
    job.totals.analyses > 0
      ? Math.round((job.completedAnalyses / job.totals.analyses) * 100)
      : 0;
  const heatmapsPercent =
    job.totals.heatmapTasks > 0
      ? Math.round(
          (job.completedHeatmapTasks / job.totals.heatmapTasks) * 100
        )
      : 0;

  return {
    phase: job.status,
    plotsPercent,
    analysesPercent,
    heatmapsPercent,
    remainingPlots: job.pendingPlotIds.length,
    remainingAnalyses: job.pendingAnalysisIds.length,
    remainingHeatmaps: job.pendingHeatmapTasks.length,
    completedPlots: job.completedPlots,
    totalPlots: job.totals.plots,
    completedAnalyses: job.completedAnalyses,
    totalAnalyses: job.totals.analyses,
    completedHeatmaps: job.completedHeatmapTasks,
    totalHeatmaps: job.totals.heatmapTasks,
  };
};
